import { useEffect, useState, useCallback } from "react"
import Constants from "expo-constants"
import { Platform } from "react-native"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRequestData } from "src/recoil/hooks/useRequestData"
import { accountSelector } from "src/recoil/selectors/account/accountSelector"
import { myOrganizationsSelector } from "src/recoil/selectors/organization/myOrganizationsSelector"
import { AdTargetingData } from "src/types/ads/AdTargetingData"

const QUESTION_IS_MARRIED = 1
const QUESTION_HAS_CHILD = 2

export const useAdTargetingData = () => {
  const [data, setData] = useState<AdTargetingData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasRunOnce, setHasRunOnce] = useState(false)

  const requestDataResult = useRequestData()
  const { value: account, isLoading: isAccountLoading } = useAsyncSelector(accountSelector)
  const { value: myOrganizations, isLoading: isMyOrganizationsLoading } = useAsyncSelector(myOrganizationsSelector)

  useEffect(() => {
    if (!account) {
      setHasRunOnce(false)
      setData(null)
    }
  }, [account])

  const fetchEndpointData = useCallback(() => {
    if (hasRunOnce) return

    setIsLoading(true)
    const isReady = requestDataResult.isOk && !isAccountLoading && !isMyOrganizationsLoading
    if (!isReady) {
      setIsLoading(false)
      return
    }

    const questionAnswers = account?.questionAnswers ?? {}
    const email = account?.email
    const isManager = myOrganizations?.some((o) => o.role === "manager") ?? false
    const joinedFreeOrganization = myOrganizations?.some((o) => !o.paidFunctionEnabled && o.showAdsFlg) ?? false

    const organizationIds = myOrganizations?.map((o) => o.id) ?? []
    const organizationPrefectures = myOrganizations?.map((o) => o.prefecture?.id).filter(Boolean) as string[] ?? []
    const organizationCategories = myOrganizations?.map((o) => o.category?.id).filter(Boolean) as string[] ?? []
    const organizationSubCategories = myOrganizations?.map((o) => o.subCategory?.id).filter(Boolean) as string[] ?? []

    const adTargetingData: AdTargetingData = {
      userAttributes: {
        accountId: account?.id,
        isManager,
        gender: account?.gender ?? "",
        job: account?.jobId ?? "",
        school: account?.schoolId ?? "",
        prefecture: account?.prefectureId ?? "",
        age: getAge(account?.birthDate).toString(),
        married: questionAnswers[QUESTION_IS_MARRIED] ?? "",
        child: questionAnswers[QUESTION_HAS_CHILD] ?? "",
        joinedFreeOrganization,
        organizationIds,
        organizationPrefectures,
        organizationCategories,
        organizationSubCategories,
        domain: email?.substring(email?.lastIndexOf("@") + 1) ?? "",
      },
      demographic: {
        platform: Platform.OS,
        appVersion: Constants.expoConfig?.version ?? "",
        model: Constants.deviceName ?? "",
      },
    }

    setData(adTargetingData)
    setHasRunOnce(true)
    setIsLoading(false)
  }, [requestDataResult, account, myOrganizations, isAccountLoading, isMyOrganizationsLoading])

  useEffect(() => {
    if (!hasRunOnce && requestDataResult.isOk && !isAccountLoading && !isMyOrganizationsLoading) {
      fetchEndpointData()
    }
  }, [fetchEndpointData, hasRunOnce, requestDataResult.isOk, isAccountLoading, isMyOrganizationsLoading])

  const refetch = useCallback(() => {
    setHasRunOnce(false)
    fetchEndpointData()
  }, [fetchEndpointData])

  return { data, isLoading, refetch }
}

const getAge = (birthDate: Date | undefined) => {
  if (!birthDate) {
    return 0
  }

  const today = new Date()
  let age = 0
  const birthYear = birthDate.getFullYear()
  const birthMonth = birthDate.getMonth()
  const birthDay = birthDate.getDate()

  age = today.getFullYear() - birthYear

  if (today.getMonth() < birthMonth || (today.getMonth() === birthMonth && today.getDate() < birthDay)) {
    age--
  }
  return age
}